import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { formatApiError } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("This reset link is missing a token. Request a new one from the sign-in screen.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true); setError("");
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password }, { timeout: 10000 });
      setDone(true);
      setTimeout(() => navigate("/?auth=login"), 2200);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--sky)] flex items-center justify-center px-4 py-12" data-testid="reset-page">
      <div className="bg-white w-full max-w-[440px] rounded-2xl shadow-xl border border-[var(--line)] overflow-hidden">
        <div className="px-7 pt-7 pb-2 flex items-center gap-3">
          <span className="inline-flex w-10 h-10 rounded-full bg-[var(--gold)] items-center justify-center">
            <ShieldCheck size={18} className="text-white" />
          </span>
          <div>
            <div className="text-[16px] font-black tracking-tight">Set a new password</div>
            <div className="text-[12px] text-[var(--muted)]">Your link is valid for 30 minutes.</div>
          </div>
        </div>
        <div className="px-7 py-5">
          {done ? (
            <div className="space-y-3" data-testid="reset-success">
              <div className="text-[14px] text-green-800 bg-green-50 border border-green-200 rounded-md px-3 py-3 font-semibold">
                Password updated. Redirecting you to sign in…
              </div>
              <Link to="/" className="text-[12px] text-[var(--blue)] hover:underline">Go home</Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3" data-testid="reset-form">
              <label className="flex items-center gap-2 border border-[var(--line)] rounded-md px-3 py-2 focus-within:border-black bg-white">
                <Lock size={14} className="text-[var(--muted)]" />
                <input
                  type="password"
                  placeholder="New password (8+ characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full text-[14px] outline-none border-0 bg-transparent"
                  data-testid="reset-password"
                  autoFocus
                />
              </label>
              <label className="flex items-center gap-2 border border-[var(--line)] rounded-md px-3 py-2 focus-within:border-black bg-white">
                <Lock size={14} className="text-[var(--muted)]" />
                <input
                  type="password"
                  placeholder="Confirm password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full text-[14px] outline-none border-0 bg-transparent"
                  data-testid="reset-confirm"
                />
              </label>
              {error && (
                <div className="text-[12.5px] text-[var(--red)] bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-2" data-testid="reset-error">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <button
                type="submit"
                disabled={busy || !token || password.length < 8 || password !== confirm}
                className="w-full bg-black text-white font-bold py-2.5 rounded-md text-[14px] hover:bg-[var(--ink)] disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="reset-submit"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : null} Update password
              </button>
              <div className="text-center text-[12px] pt-1">
                <Link to="/" className="text-[var(--muted)] hover:text-black" data-testid="reset-back-home">Back to Newseze</Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
